import { makeAutoObservable, runInAction } from 'mobx';
import { llmPricingApi } from '@/services/llmPricingApi';
import type {
  AppConfigVersionMeta,
  LLMConfigValidation,
  PricingCatalog,
  PricingRule,
} from '@/types';

class LLMPricingStore {
  catalog: PricingCatalog | null = null;
  originalCatalog: PricingCatalog | null = null;
  baseVersion = 0;
  latestHostedVersion = 0;
  currentVersion: AppConfigVersionMeta | null = null;
  recentHostedVersions: AppConfigVersionMeta[] = [];
  recentDeployments: AppConfigVersionMeta[] = [];
  validation: LLMConfigValidation | null = null;
  warning = '';
  loading = false;
  publishing = false;
  error = '';
  keyword = '';
  providerFilter = 'tencent_tokenhub';
  publishResult: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  get deploymentPending(): boolean {
    return this.recentDeployments.some((deployment) =>
      ['BAKING', 'DEPLOYING', 'VALIDATING'].includes(
        (deployment.deployment_state || '').toUpperCase(),
      ),
    );
  }

  setKeyword(value: string) {
    this.keyword = value;
  }

  setProviderFilter(value: string) {
    this.providerFilter = value;
  }

  get filteredRules(): PricingRule[] {
    if (!this.catalog) return [];
    const kw = this.keyword.trim().toLowerCase();
    const prov = this.providerFilter.trim().toLowerCase();
    return this.catalog.rules.filter((rule) => {
      if (prov && (rule.provider || '').toLowerCase() !== prov) return false;
      if (!kw) return true;
      return [
        rule.id,
        rule.public_model,
        rule.upstream_model,
        rule.provider_path,
        rule.route_id,
      ]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(kw));
    });
  }

  get distinctProviders(): string[] {
    if (!this.catalog) return [];
    const set = new Set<string>();
    this.catalog.rules.forEach((r) => {
      if (r.provider) set.add(r.provider);
    });
    return Array.from(set).sort();
  }

  async loadCatalog() {
    this.loading = true;
    this.error = '';
    try {
      const view = await llmPricingApi.getPricingCatalog();
      runInAction(() => {
        this.catalog = view.document;
        this.originalCatalog = structuredClone(view.document);
        this.baseVersion = view.baseVersion;
        this.latestHostedVersion = view.latestHostedVersion;
        this.currentVersion = view.currentVersion;
        this.recentHostedVersions = view.recentHostedVersions;
        this.recentDeployments = view.recentDeployments;
        this.warning = view.validationWarning || '';
        this.validation = null;
        this.publishResult = null;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : '加载价格表失败';
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async validateCatalog(document: PricingCatalog): Promise<boolean> {
    this.publishing = true;
    this.error = '';
    try {
      const validation = await llmPricingApi.validatePricingCatalog(document);
      runInAction(() => {
        this.validation = validation;
      });
      return true;
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : '校验价格表失败';
      });
      return false;
    } finally {
      runInAction(() => {
        this.publishing = false;
      });
    }
  }

  async publishCatalog(document: PricingCatalog, versionLabel?: string) {
    this.publishing = true;
    this.error = '';
    try {
      await llmPricingApi.publishPricingCatalog({
        document,
        baseVersion: this.baseVersion,
        versionLabel,
      });
      await this.loadCatalog();
      runInAction(() => {
        this.publishResult = '发布成功';
      });
    } catch (err) {
      runInAction(() => {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        this.error =
          status === 409
            ? '配置已被其他管理员更新，请刷新页面后重新确认 Diff'
            : err instanceof Error
              ? err.message
              : '发布价格表失败';
      });
    } finally {
      runInAction(() => {
        this.publishing = false;
      });
    }
  }

  async rollbackCatalog(targetVersion: number): Promise<boolean> {
    const baseVersion = this.latestHostedVersion || this.baseVersion;
    if (baseVersion <= 0) return false;
    this.publishing = true;
    this.error = '';
    try {
      await llmPricingApi.rollbackPricingCatalog({
        targetVersion,
        baseVersion,
      });
      await this.loadCatalog();
      return true;
    } catch (err) {
      runInAction(() => {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        this.error =
          status === 409
            ? '配置已被其他管理员更新，请刷新页面后重新确认 Diff'
            : err instanceof Error
              ? err.message
              : '价格表回滚失败';
      });
      return false;
    } finally {
      runInAction(() => {
        this.publishing = false;
      });
    }
  }

  updateRule(ruleId: string, patch: Partial<PricingRule>) {
    if (!this.catalog) return;
    this.catalog = {
      ...this.catalog,
      rules: this.catalog.rules.map((r) =>
        r.id === ruleId ? { ...r, ...patch } : r,
      ),
    };
    this.validation = null;
  }

  addRule(rule: PricingRule) {
    if (!this.catalog) return;
    this.catalog = {
      ...this.catalog,
      rules: [...this.catalog.rules, rule],
    };
    this.validation = null;
  }

  deleteRule(ruleID: string) {
    if (!this.catalog) return;
    this.catalog = {
      ...this.catalog,
      rules: this.catalog.rules.filter((rule) => rule.id !== ruleID),
    };
    this.validation = null;
  }
}

export const llmPricingStore = new LLMPricingStore();
